/**
 * Beside the thread — `docs/activity.md` §7.1 (Board 7, 300 wide): the
 * contact, four 34 px buttons (call, text, email, open the contact), the
 * **Property**, the **Jobs**, **Next up** with **Move**, and **Make** — Task ·
 * Meeting · Log a call · Note, each opening its sheet attached as the thread
 * is. Absent for a number Rowted doesn't know (Board 8 B).
 */
import { useNavigate } from 'react-router-dom'
import { KIND_STYLES, type ConversationThread, type InboxChannel } from '@rowted/shared'
import { StatusChip } from '../../components/primitives.js'
import { cn } from '../../lib/cn.js'
import { Glyph, type GlyphName } from '../glyphs.js'
import { BUTTON, Initials, PLAIN, PanelHead, clockShort, localOf, monthDay } from './look.js'
import { makeFromThread, moveItem, type MakeKind } from './actions.js'

/* Board 7: 34 px inside a hairline — 36 across. */
const SQUARE = 'inline-flex h-9 w-9 items-center justify-center rounded-[10px] border border-line text-accent hover:bg-hover'

const MAKE: { kind: MakeKind; label: string; glyph: GlyphName }[] = [
  { kind: 'task', label: 'Task', glyph: 'check_square' },
  { kind: 'meeting', label: 'Meeting', glyph: 'calendar' },
  { kind: 'call', label: 'Log a call', glyph: 'phone' },
  { kind: 'note', label: 'Note', glyph: 'pencil' },
]

/** *Hardscape · by Wed, Nov 25* — `wanted` is `wantedText()`'s, which already says *by*. */
function jobLine(job: { division: string | null; wanted: string | null }): string {
  return [job.division, job.wanted].filter(Boolean).join(' · ')
}

export function ContactPanel({
  thread, today, onChannel,
}: { thread: ConversationThread; today: string; onChannel: (channel: InboxChannel) => void }) {
  const navigate = useNavigate()
  const contact = thread.contact
  if (!contact) return null
  const next = contact.nextUp
  const nextLocal = next?.startsAt ? localOf(next.startsAt, thread.timeZone) : null
  const nextWhat = next
    ? next.kind === 'appointment' ? KIND_STYLES.site_visit.label : next.kind === 'meeting' ? KIND_STYLES.meeting.label : next.title
    : ''
  const nextWords = next
    ? [nextLocal
      ? next.minutes ? `${clockShort(nextLocal.minutes)}–${clockShort(nextLocal.minutes + next.minutes)}` : clockShort(nextLocal.minutes)
      : null, next.confirmed ? 'Confirmed' : null].filter(Boolean).join(' · ')
    : ''

  return (
    /* `leading-[normal]`: Board 7's panel sets no line-height of its own, so its lines sit at the face's. */
    <aside className="w-[300px] shrink-0 self-stretch overflow-y-auto border-l border-line bg-surface p-4 leading-[normal]" aria-label="The customer">
      <div className="flex items-center gap-2.5">
        <Initials row={thread.row} size={44} />
        <div className="flex min-w-0 flex-col">
          <b className="truncate text-[15px]">{contact.name}</b>
          <span className="text-[12px] text-text-muted">
            Customer{contact.since ? ` · since ${monthDay(contact.since)}` : ''}
          </span>
        </div>
      </div>
      <div className="mt-2.5 text-[12.5px] leading-[1.6] text-text-body">
        {contact.phone && <div>{contact.phone}{contact.phoneKind ? ` · ${contact.phoneKind}` : ''}</div>}
        {contact.email && <div className="truncate">{contact.email}</div>}
        {contact.prefers && <div>Prefers {contact.prefers}</div>}
      </div>
      <div className="mt-2.5 flex items-center gap-1.5">
        {contact.phone && (
          <a href={`tel:${contact.phone.replace(/[^\d+]/g, '')}`} aria-label={`Call ${contact.firstName}`} className={SQUARE}>
            <Glyph name="phone" size={16} />
          </a>
        )}
        {thread.composer.channels.includes('sms') && (
          <button type="button" aria-label={`Text ${contact.firstName}`} className={SQUARE} onClick={() => onChannel('sms')}>
            <Glyph name="bubble" size={16} />
          </button>
        )}
        {thread.composer.channels.includes('email') && (
          <button type="button" aria-label={`Email ${contact.firstName}`} className={SQUARE} onClick={() => onChannel('email')}>
            <Glyph name="envelope" size={16} />
          </button>
        )}
        <button type="button" aria-label={`Open ${contact.name}`} className={SQUARE} onClick={() => navigate(`/contacts/${contact.id}`)}>
          <Glyph name="user" size={16} />
        </button>
      </div>

      {contact.property && (
        <>
          <PanelHead>Property</PanelHead>
          <button type="button" className="block w-full text-left text-[13px]" onClick={() => navigate(`/properties/${contact.property!.id}`)}>
            <b className="block truncate">{contact.property.name}</b>
            <span className="block truncate text-[12px] text-text-muted">{contact.property.line}</span>
          </button>
        </>
      )}

      {contact.jobs.length > 0 && (
        <>
          <PanelHead>Jobs · {contact.jobs.length}</PanelHead>
          <div className="flex flex-col gap-2.5">
            {contact.jobs.map((job) => (
              <button key={job.id} type="button" onClick={() => navigate(`/jobs/${job.id}`)}
                className="flex w-full items-center gap-2 text-left text-[13px]">
                <span className="flex min-w-0 flex-1 flex-col">
                  <b className="truncate">{job.reference} · {job.title}</b>
                  {jobLine(job) && <span className="truncate text-[12px] text-text-muted">{jobLine(job)}</span>}
                </span>
                {/*
                  * Board 7's chip: 20 high, 9 px sides, 10.5/700, a hairline in its own
                  * colour and a 7 px dot. `!` because the chip's own sizes otherwise win
                  * (`cn` joins, the stylesheet decides). *Requested* is blue here, as
                  * Board 7 draws it (Aaron, Oct 23: "blue is fine") — this chip only;
                  * the job lifecycle's own grey stays everywhere else.
                  */}
                <StatusChip group="job" status={job.status}
                  className={cn('!h-5 shrink-0 !gap-[5px] !rounded-full border !px-[9px] !text-[10.5px] !font-bold',
                    '[border-color:color-mix(in_srgb,currentColor_22%,transparent)] [&>span]:!h-[7px] [&>span]:!w-[7px]',
                    job.status === 'requested' && '!border-info-line !bg-info-bg !text-info [&>span]:!bg-info')} />
              </button>
            ))}
          </div>
        </>
      )}

      {next && (
        <>
          <PanelHead>Next up</PanelHead>
          <div className="flex items-center gap-2.5 rounded-[10px] border border-accent-line bg-green-band-bg p-2.5">
            {/* Board 7: 38 × 40 inside a hairline — 40 × 42. */}
            <span className="inline-flex h-[42px] w-10 shrink-0 flex-col items-center justify-center rounded-lg border border-accent-line bg-surface">
              <span className="text-[9px] font-bold uppercase text-accent-dark">
                {nextLocal ? (nextLocal.date === today ? 'TODAY' : monthDay(nextLocal.date).toUpperCase()) : ''}
              </span>
              <b className="text-[13px] leading-tight text-accent-dark">{nextLocal ? clockShort(nextLocal.minutes) : ''}</b>
            </span>
            <span className="flex min-w-0 flex-1 flex-col">
              <b className="truncate text-[12.5px]">{nextWhat}{next.who ? ` · ${next.who}` : ''}</b>
              {nextWords && <span className="truncate text-[11.5px] text-text-muted">{nextWords}</span>}
            </span>
            <button type="button" onClick={() => void moveItem(next.kind, next.id)}
              className="inline-flex h-6 shrink-0 items-center rounded-lg px-1.5 text-[12px] font-semibold text-accent hover:bg-surface">
              Move
            </button>
          </div>
        </>
      )}

      <PanelHead>Make</PanelHead>
      <div className="flex flex-wrap gap-1.5">
        {MAKE.map((one) => (
          <button key={one.kind} type="button" onClick={() => makeFromThread(one.kind, thread)}
            className={cn(BUTTON, PLAIN, 'h-[30px] px-3.5 text-[12px]')}>
            <Glyph name={one.glyph} size={14} />{one.label}
          </button>
        ))}
      </div>
    </aside>
  )
}
